import { getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js'
import { z } from 'zod'

/**
 * Deciding which country code to assume when somebody types a phone number without one.
 *
 * The decision is data, not code: it lives in `shared/phoneCountryCodes.yaml` so it can be
 * corrected by editing a file rather than by shipping new logic. This module only reads
 * that file's shape and applies it.
 */

export const CONTINENT_CODES = ['AF', 'AN', 'AS', 'EU', 'NA', 'OC', 'SA'] as const
export type ContinentCode = (typeof CONTINENT_CODES)[number]

export const CONTINENT_NAMES: Readonly<Record<ContinentCode, string>> = {
  AF: 'Africa',
  AN: 'Antarctica',
  AS: 'Asia',
  EU: 'Europe',
  NA: 'North America',
  OC: 'Oceania',
  SA: 'South America',
}

/** Says to use the calling code of the country the request actually came from. */
export const CALLING_CODE_FROM_COUNTRY = 'from-country'

const ANY = '*'

export const phoneCountryCodeRuleSchema = z.object({
  /** The language this deployment serves, or `*` for any. */
  locale: z.string().min(1),
  /** The continent the request came from, or `*` for any. */
  continent: z.union([z.enum(CONTINENT_CODES), z.literal(ANY)]),
  /** The country the request came from, or `*` for any. */
  country: z.string().min(1),
  /** `+55`, or `from-country`. */
  callingCode: z
    .string()
    .min(1)
    .refine(
      (callingCode) =>
        callingCode === CALLING_CODE_FROM_COUNTRY || /^\+[0-9]{1,4}$/.test(callingCode),
      { message: `A calling code is "+" and up to four digits, or "${CALLING_CODE_FROM_COUNTRY}".` },
    ),
})
export type PhoneCountryCodeRule = z.infer<typeof phoneCountryCodeRuleSchema>

export const phoneCountryCodeMapSchema = z.object({
  rules: z.array(phoneCountryCodeRuleSchema),
  // Partial on purpose: a map only has to classify the continents its rules name, and a
  // deployment that never asks about Antarctica should not have to list it.
  continents: z.partialRecord(z.enum(CONTINENT_CODES), z.array(z.string().length(2))),
})
export type PhoneCountryCodeMap = z.infer<typeof phoneCountryCodeMapSchema>

/** Where a request came from, as far as anything can tell. Both parts may be unknown. */
export type RequestOrigin = {
  countryCode: string | null
}

export function findContinentOfCountry(
  countryCode: string,
  countryCodeMap: PhoneCountryCodeMap,
): ContinentCode | null {
  const upperCaseCountry = countryCode.toUpperCase()
  for (const continentCode of CONTINENT_CODES) {
    if (countryCodeMap.continents[continentCode]?.includes(upperCaseCountry) === true) {
      return continentCode
    }
  }
  return null
}

function ruleMatches(
  rule: PhoneCountryCodeRule,
  deploymentLocale: string,
  countryCode: string | null,
  continentCode: ContinentCode | null,
): boolean {
  if (rule.locale !== ANY && rule.locale !== deploymentLocale) {
    return false
  }
  // An unknown origin cannot satisfy a rule that asks about one. Saying "no" here is what
  // keeps a deployment without a proxy header from silently assuming somebody's country.
  if (rule.continent !== ANY && rule.continent !== continentCode) {
    return false
  }
  if (rule.country !== ANY && rule.country.toUpperCase() !== countryCode?.toUpperCase()) {
    return false
  }
  return true
}

/**
 * The calling code to assume for this deployment and this request, or `null` when no rule
 * covers it — in which case a number must carry its own country code, exactly as before.
 *
 * Rules are read in file order and the first match wins, so precedence is what the reader
 * sees rather than a hidden notion of which rule is more specific.
 */
export function resolveDefaultCallingCode(resolution: {
  deploymentLocale: string
  origin: RequestOrigin
  countryCodeMap: PhoneCountryCodeMap
}): string | null {
  const { deploymentLocale, origin, countryCodeMap } = resolution
  const countryCode = origin.countryCode?.toUpperCase() ?? null
  const continentCode =
    countryCode === null ? null : findContinentOfCountry(countryCode, countryCodeMap)

  for (const rule of countryCodeMap.rules) {
    if (!ruleMatches(rule, deploymentLocale, countryCode, continentCode)) {
      continue
    }

    if (rule.callingCode !== CALLING_CODE_FROM_COUNTRY) {
      return rule.callingCode
    }
    // "from-country" needs a country to read it from, and a rule that cannot be satisfied
    // is passed over rather than answered with something invented.
    if (countryCode !== null && isSupportedCountry(countryCode)) {
      return `+${getCountryCallingCode(countryCode)}`
    }
  }

  return null
}
